<template>
  <div
    v-if="preLoading"
    class="flex items-center justify-center h-svh font-bold animate-pulse text-[#222222]"
  >
    {{ $t('loading') }}
  </div>
  <div v-else class="flex flex-col justify-between h-full">
    <Launch
      v-if="initializingApp"
      :brandLogo="brandingTheme?.logo"
      :deviceInfo="deviceInfo"
      @initializeApp="initializeApp"
    />
    <div
      v-else
      class="flex flex-col overflow-y-auto hide-scrollbar rtl:text-right rtl:overflow-x-hidden"
    >
      <div class="flex flex-col grow">
        <Header
          v-if="!hideHeader"
          :brandingTheme="brandingTheme"
          :is-stc="isStc"
        />
        <MainContent
          ref="mainContentRef"
          :decodedData="decodedData"
          :deviceInfo="deviceInfo"
          :is-stc="isStc"
          :isHybrid="isHybrid"
          :queryScanId="queryScanId"
        />
      </div>
    </div>
    <Footer
      :class="{ 'bg-[var(--background)] text-[var(--text)]': initializingApp }"
      :is-stc="isStc"
      :version="appVersion"
    />
    <OrientationModal v-if="deviceInfo?.isMobileOrTablet()" />
  </div>
</template>

<script setup>
import { computed, onBeforeMount, provide, ref, watchEffect } from 'vue'
import { getDeviceInfo } from '@/utils/deviceInfo.js'
import Launch from '@/components/Launch.vue'
import Footer from '@/components/Footer.vue'
import Header from '@/components/Header.vue'
import MainContent from '@/components/MainContent.vue'
import OrientationModal from '@/components/OrientationModal.vue'
import { useI18n } from 'vue-i18n'
import {
  getBrandingConfiguration,
  getBrandingConfigurationByKey
} from '@/utils/getBrandingConfiguration.js'
import * as Sentry from '@sentry/vue'

const appVersion = __APP_VERSION__

const isStc = ref(false)
const isHybrid = ref(false)
const hideHeader = ref(false)
const decodedData = ref({})
const queryScanId = ref(null)
const preLoading = ref(true)
const isDataFromMobileApp = ref(false)
const deviceInfo = ref(null)
const { locale } = useI18n()
const brandingTheme = ref({})
const initializingApp = ref(true)

provide('brandingTheme', brandingTheme)

const isRtl = computed(() => ['fa'].includes(locale.value))

const updateRtlAttribute = () => {
  document.documentElement.setAttribute('lang', locale.value)
  document.documentElement.setAttribute('dir', isRtl.value ? 'rtl' : 'ltr')
}

const initializeApp = async () => {
  initializingApp.value = false
}

onBeforeMount(async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const encodeDataFromUrl = urlParams.get('settings')
  const encodeDataFromApp = urlParams.get('appSettings')
  queryScanId.value = urlParams.get('uid')
  const apiKey = urlParams.get('api_key')
  const lpKey = urlParams.get('lp_key')

  try {
    deviceInfo.value = await getDeviceInfo()
  } catch (error) {
    console.error('Failed to get device info:', error)
    Sentry.captureException(new Error('Failed to get device info'), {
      cause: error
    })
    deviceInfo.value = null
  }

  try {
    if (lpKey) {
      brandingTheme.value = await getBrandingConfigurationByKey(lpKey)
    } else if (queryScanId.value && apiKey) {
      brandingTheme.value = await getBrandingConfiguration(
        queryScanId.value,
        apiKey
      )
    }

    if (Object.keys(brandingTheme.value).length !== 0) {
      document.title = 'Authentication'
    }

    if (brandingTheme.value?.forceDefaultLanguage) {
      locale.value = brandingTheme.value.forceDefaultLanguage
    }
  } catch (error) {
    console.error('Error setting branding theme:', error)
    brandingTheme.value = null
  }

  preLoading.value = false

  if (encodeDataFromUrl) {
    decodedData.value = decodeDataFromQueryParameters(encodeDataFromUrl)
  } else if (encodeDataFromApp) {
    isDataFromMobileApp.value = true
    decodedData.value = decodeDataFromQueryParameters(encodeDataFromApp)
    decodedData.value.isDataFromMobileApp = isDataFromMobileApp.value
  }
  if (urlParams.get('is_stc') === 'true') {
    isStc.value = true
  }
  if (urlParams.get('sg_outside') === 'true') {
    isHybrid.value = true
  }
  if (urlParams.has('no-header')) {
    hideHeader.value = true
  }
  updateRtlAttribute()
})

watchEffect(() => {
  updateRtlAttribute()
})

const decodeDataFromQueryParameters = (data) => {
  const base64String = decodeURIComponent(data)
  const jsonString = atob(base64String)

  if (isDataFromMobileApp.value) {
    const params = new URLSearchParams(jsonString)
    const jsonObject = {}

    params.forEach((value, key) => {
      jsonObject[key] = value
    })

    return jsonObject
  } else {
    return JSON.parse(jsonString)
  }
}
</script>
